import React, { useState } from "react"

import { OldButton } from "@kit/design/old-button"
import Stack from "@kit/design/stack"
import Grid from "@kit/design/grid"
import { toast } from "@kit/design/toast"

import { Alignment } from "core/alignment"
import { Field } from "core/form"
import { Input } from "core/deprecated-inputs"
import { isValidEmbedUrl } from "core/editor"
import { useFetch } from "hooks/use-new-fetch"

import "./index.css"

const isValidWidth = width => {
  if (width) {
    const intWidth = parseInt(width)
    return intWidth >= 100 && intWidth <= 480
  } else {
    return false
  }
}

const isIntOrEmpty = (value, validity) => {
  const stringValue = value.toString()
  return (
    (stringValue === "" && validity) ||
    (stringValue.match(/^\d+$/) && stringValue.indexOf(".") === -1)
  )
}

export const VideoEmbedMenu = ({ editor, node }) => {
  const video = node
  const [embedRequest, loading] = useFetch("/api/v3/embed")
  const [href, setHref] = useState(video.data.get("href") || "")
  const [width, setWidth] = useState(parseInt(video.data.get("width")) || 480)
  const alignment = video.data.get("alignment") || "center"

  const isDirty = () => {
    return href !== video.data.get("href") || width != video.data.get("width")
  }

  const isUpdateEnabled = () => {
    return isDirty() && isValidEmbedUrl(href) && isValidWidth(width) && !loading
  }

  const setVideoData = data => {
    editor.command("setNodeByKey", video.key, {
      data: { ...video.data.toJS(), ...data }
    })
  }

  const setVideoDataFromTarget = ({ name, value }) => {
    setVideoData({ [name]: value })
  }

  const handleWidthChange = event => {
    const value = event.target.value
    const validity = event.target.validity.valid
    if (isIntOrEmpty(value, validity)) {
      setWidth(value)
    }
  }

  const handleHrefChange = event => {
    setHref(event.target.value)
  }

  const handleUpdate = async () => {
    try {
      const { data } = await embedRequest.get({
        queryParams: { url: href, maxwidth: width }
      })

      setVideoData({ embedHtml: data.html, width, href })
    } catch (_) {
      toast({
        type: "error",
        text: "There was a problem embedding the video. Try again later or contact us!"
      })
    }
  }

  const handleHrefKeyDown = event => {
    if (event.key === "Enter") event.preventDefault()
  }

  return (
    <Stack style={{ display: "flex", padding: 0, minWidth: 300 }}>
      <Field
        control={Input}
        label="Video URL"
        name="href"
        value={href}
        variant="dark"
        onChange={handleHrefChange}
        onKeyDown={handleHrefKeyDown}
      />
      {!isValidEmbedUrl(href) && (
        <p style={{ fontSize: "0.75em" }}>
          This URL is not a YouTube or Vimeo link
        </p>
      )}
      <Grid columns="repeat(2, 120px)" gap=".5rem" rowGap=".75rem">
        <div>
          <Field
            control={Input}
            label="Video width"
            type="number"
            name="width"
            variant="dark"
            min={100}
            max={480}
            value={width}
            onChange={handleWidthChange}
          />
          {!isValidWidth(width) && (
            <p style={{ fontSize: "0.75em" }}>
              Width must be between 100 and 480
            </p>
          )}
        </div>
        <Stack spacing={2}>
          <div className="text-sm font-semibold leading-snug text-gray-700">
            Alignment
          </div>
          <Alignment
            alignment={alignment}
            onChange={target => setVideoDataFromTarget(target)}
          />
        </Stack>
      </Grid>
      <OldButton
        onClick={handleUpdate}
        disabled={!isUpdateEnabled()}
        is="primary"
        size="sm"
        style={{ marginLeft: "auto", marginRight: "0" }}
      >
        {loading ? "Loading" : "Update"}
      </OldButton>
    </Stack>
  )
}
